import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, User, Lock, Sparkles, Check, ChevronLeft, ChevronRight,
  ArrowUpRight, ShieldCheck, AlertCircle, Loader2,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: "Firm",       icon: Building2 },
  { id: 2, label: "Owner",      icon: User },
  { id: 3, label: "Password",   icon: Lock },
  { id: 4, label: "Launch",     icon: Sparkles },
];

const PLAN_LABEL = {
  starter: "Starter · ₹4,999/mo",
  pro: "Pro · ₹8,999/mo",
  enterprise: "Enterprise · ₹16,999/mo",
};

const inp =
  "w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.05] transition";

const lbl = "block text-xs uppercase tracking-[0.18em] text-white/55 mb-1.5";

export default function StartTrial() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { setUserAndTokenAfterOnboarding } = useAuth(); // we'll add this helper
  const initialPlan = new URLSearchParams(loc.search).get("plan") || "starter";

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    firm_name: "",
    arn_code: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    password: "",
    confirm: "",
    plan: initialPlan,
  });
  const [emailStatus, setEmailStatus] = useState({ checking: false, available: null, reason: null });
  const [submitting, setSubmitting] = useState(false);
  const emailCheckTimer = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ─── Real-time email availability check (Step 2) ───
  useEffect(() => {
    if (step !== 2) return;
    const email = (form.owner_email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setEmailStatus({ checking: false, available: null, reason: null });
      return;
    }
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
    emailCheckTimer.current = setTimeout(async () => {
      setEmailStatus((s) => ({ ...s, checking: true }));
      try {
        const r = await axios.get(`${API}/api/public/check-email`, { params: { email } });
        setEmailStatus({ checking: false, available: !!r.data.available, reason: r.data.reason || null });
      } catch {
        setEmailStatus({ checking: false, available: null, reason: null });
      }
    }, 500);
    return () => emailCheckTimer.current && clearTimeout(emailCheckTimer.current);
  }, [form.owner_email, step]);

  const pwdScore = useMemo(() => {
    const p = form.password || "";
    let s = 0;
    if (p.length >= 8) s += 1;
    if (p.length >= 12) s += 1;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
    if (/\d/.test(p)) s += 1;
    if (/[^A-Za-z0-9]/.test(p)) s += 1;
    return s; // 0..5
  }, [form.password]);

  // ─── Step validators ───
  const stepOk = (s) => {
    if (s === 1) return form.firm_name.trim().length >= 2;
    if (s === 2) {
      return (
        form.owner_name.trim().length >= 2 &&
        /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.owner_email.trim()) &&
        emailStatus.available !== false
      );
    }
    if (s === 3) {
      return form.password.length >= 8 && form.password === form.confirm;
    }
    return true;
  };

  const next = () => {
    if (!stepOk(step)) {
      toast.error(stepOk(step) ? "" : "Please fill all required fields correctly");
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  // ─── Submit ───
  const submit = async () => {
    if (!stepOk(1) || !stepOk(2) || !stepOk(3)) {
      toast.error("Please complete all steps first");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/public/onboard-tenant`, {
        firm_name: form.firm_name.trim(),
        arn_code: (form.arn_code || "").trim() || null,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_phone: (form.owner_phone || "").trim() || null,
        password: form.password,
        plan: form.plan,
      });
      // Persist the issued token + user so the app is logged in
      // before the redirect — no second login required.
      localStorage.setItem("token", r.data.access_token);
      try {
        // Optional hook on AuthContext (added below). Falls back to a
        // hard reload if the hook isn't available (zero-risk path).
        if (typeof setUserAndTokenAfterOnboarding === "function") {
          setUserAndTokenAfterOnboarding(r.data.access_token, r.data.user);
        }
      } catch (_) {/* noop */}
      toast.success(`Welcome ${r.data.user.name.split(" ")[0]} — your 14-day trial is live!`);
      // Hard navigation so the entire app re-bootstraps under the new token
      window.location.replace("/dashboard?welcome=1");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create your trial — please try again");
      // If duplicate, push them to login
      if (e?.response?.status === 409) {
        setTimeout(() => navigate(`/login?email=${encodeURIComponent(form.owner_email)}`), 1500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout title="Start your free trial">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-20" data-testid="start-trial-page">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40 mb-5">
            <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
            14-day free trial
            <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-light leading-tight">
            Step into the platform —<br />
            <span className="text-[#C8A96B]">no credit card required.</span>
          </h1>
          <p className="text-white/55 text-sm mt-4 max-w-lg mx-auto">
            We&apos;ll provision a private workspace for your firm in under 30 seconds.
            Upload your Karvy / CAMS reports and start consolidating client portfolios immediately.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10 px-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className="flex items-center" style={{ flex: i === STEPS.length - 1 ? "0 0 auto" : "1 1 auto" }}>
                <div className="flex flex-col items-center">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center border transition ${
                      done
                        ? "bg-[#C8A96B] text-[#07111F] border-[#C8A96B]"
                        : active
                        ? "border-[#C8A96B] text-[#C8A96B] bg-[#C8A96B]/10"
                        : "border-white/15 text-white/40"
                    }`}
                    data-testid={`step-indicator-${s.id}`}
                  >
                    {done ? <Check size={16} /> : <Icon size={16} />}
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.18em] mt-2 ${active ? "text-[#C8A96B]" : "text-white/40"}`}>
                    {s.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-[1px] mx-3 transition flex-1 ${done ? "bg-[#C8A96B]" : "bg-white/10"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="border border-white/10 rounded-2xl bg-white/[0.02] p-8 sm:p-10">
          {/* STEP 1 — Firm details */}
          {step === 1 && (
            <div data-testid="step-firm">
              <h2 className="text-base sm:text-lg font-medium mb-1">Tell us about your firm</h2>
              <p className="text-xs text-white/45 mb-7">This becomes the workspace name your team will see.</p>
              <div className="space-y-5">
                <div>
                  <label className={lbl}>Firm name<span className="text-[#C8A96B]"> *</span></label>
                  <input data-testid="firm-name-input" className={inp} value={form.firm_name} onChange={update("firm_name")} placeholder="e.g. Acme Wealth Advisors" autoFocus />
                </div>
                <div>
                  <label className={lbl}>ARN code <span className="text-white/30 normal-case tracking-normal text-[11px]">(optional — you can add later)</span></label>
                  <input data-testid="arn-code-input" className={inp} value={form.arn_code} onChange={update("arn_code")} placeholder="e.g. ARN-12345" />
                </div>
                <div>
                  <label className={lbl}>Plan</label>
                  <div className="flex gap-2 flex-wrap">
                    {["starter", "pro", "enterprise"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, plan: p }))}
                        className={`px-4 py-2 rounded-lg text-xs uppercase tracking-[0.16em] border transition ${
                          form.plan === p
                            ? "border-[#C8A96B] text-[#C8A96B] bg-[#C8A96B]/10"
                            : "border-white/10 text-white/50 hover:border-white/25"
                        }`}
                        data-testid={`plan-pick-${p}`}
                      >
                        {PLAN_LABEL[p]}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-white/40 mt-2">No payment now. Pick anything — billing only starts after your trial ends.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Owner details */}
          {step === 2 && (
            <div data-testid="step-owner">
              <h2 className="text-base sm:text-lg font-medium mb-1">Who&apos;s the workspace owner?</h2>
              <p className="text-xs text-white/45 mb-7">You&apos;ll log in with this email. You can invite team-mates from inside the app later.</p>
              <div className="space-y-5">
                <div>
                  <label className={lbl}>Your name<span className="text-[#C8A96B]"> *</span></label>
                  <input data-testid="owner-name-input" className={inp} value={form.owner_name} onChange={update("owner_name")} placeholder="e.g. Priya Sharma" autoFocus />
                </div>
                <div>
                  <label className={lbl}>Email<span className="text-[#C8A96B]"> *</span></label>
                  <input data-testid="owner-email-input" type="email" className={inp} value={form.owner_email} onChange={update("owner_email")} placeholder="priya@acme.com" />
                  {form.owner_email && emailStatus.checking && (
                    <div className="text-[11px] text-white/45 mt-1.5 flex items-center gap-1.5">
                      <Loader2 size={12} className="animate-spin" /> Checking availability…
                    </div>
                  )}
                  {form.owner_email && !emailStatus.checking && emailStatus.available === true && (
                    <div className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1.5" data-testid="email-available">
                      <Check size={12} /> Email is available
                    </div>
                  )}
                  {form.owner_email && !emailStatus.checking && emailStatus.available === false && (
                    <div className="text-[11px] text-amber-300 mt-1.5 flex items-start gap-1.5" data-testid="email-taken">
                      <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                      <span>
                        {emailStatus.reason === "tenant_exists"
                          ? "A trial already exists for this email. "
                          : "An account with this email exists. "}
                        <Link to={`/login?email=${encodeURIComponent(form.owner_email)}`} className="text-[#C8A96B] underline underline-offset-2">
                          Log in instead →
                        </Link>
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label className={lbl}>Mobile <span className="text-white/30 normal-case tracking-normal text-[11px]">(optional)</span></label>
                  <input data-testid="owner-phone-input" className={inp} value={form.owner_phone} onChange={update("owner_phone")} placeholder="+91 98765 43210" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Password */}
          {step === 3 && (
            <div data-testid="step-password">
              <h2 className="text-base sm:text-lg font-medium mb-1">Set your password</h2>
              <p className="text-xs text-white/45 mb-7">Minimum 8 characters. We&apos;ll log you in immediately after.</p>
              <div className="space-y-5">
                <div>
                  <label className={lbl}>Password<span className="text-[#C8A96B]"> *</span></label>
                  <input data-testid="password-input" type="password" className={inp} value={form.password} onChange={update("password")} placeholder="At least 8 characters" autoFocus />
                  {form.password && (
                    <div className="mt-2 flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded transition ${
                            i <= pwdScore
                              ? pwdScore <= 2
                                ? "bg-amber-400"
                                : pwdScore === 3
                                ? "bg-yellow-300"
                                : "bg-emerald-400"
                              : "bg-white/10"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className={lbl}>Confirm password<span className="text-[#C8A96B]"> *</span></label>
                  <input data-testid="confirm-input" type="password" className={inp} value={form.confirm} onChange={update("confirm")} placeholder="Type it again" />
                  {form.confirm && form.password !== form.confirm && (
                    <div className="text-[11px] text-amber-300 mt-1.5">Passwords don&apos;t match yet.</div>
                  )}
                </div>
                <div className="flex items-start gap-2 text-[11px] text-white/45 pt-1">
                  <ShieldCheck size={14} className="flex-shrink-0 mt-0.5 text-[#C8A96B]" />
                  <span>Your password is hashed with bcrypt before it ever touches our database. Kinntegra staff cannot read it.</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 — Review + Launch */}
          {step === 4 && (
            <div data-testid="step-launch">
              <h2 className="text-base sm:text-lg font-medium mb-1">Ready to launch your workspace</h2>
              <p className="text-xs text-white/45 mb-7">Quick recap — hit Launch to drop into the dashboard.</p>
              <dl className="divide-y divide-white/5 border border-white/10 rounded-xl overflow-hidden">
                {[
                  ["Firm", form.firm_name],
                  ["ARN", form.arn_code || "—"],
                  ["Plan", PLAN_LABEL[form.plan] || form.plan],
                  ["Owner", form.owner_name],
                  ["Email", form.owner_email],
                  ["Mobile", form.owner_phone || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center px-5 py-3 text-sm">
                    <dt className="text-white/45 text-xs uppercase tracking-[0.16em]">{k}</dt>
                    <dd className="text-white font-medium truncate ml-4">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-[11px] text-white/40 mt-5">
                By clicking Launch you accept our trial terms: 14 days of full access, no payment now,
                we&apos;ll email you 3 days before the trial ends.
              </p>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-9 pt-6 border-t border-white/5">
            <button
              type="button"
              onClick={back}
              disabled={step === 1}
              className="text-xs uppercase tracking-[0.18em] text-white/45 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 transition"
              data-testid="back-button"
            >
              <ChevronLeft size={14} /> Back
            </button>

            {step < 4 && (
              <button
                type="button"
                onClick={next}
                disabled={!stepOk(step)}
                className="bg-[#C8A96B] text-[#07111F] hover:bg-[#d4b87a] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium flex items-center gap-1.5 transition"
                data-testid="next-button"
              >
                Continue <ChevronRight size={14} />
              </button>
            )}
            {step === 4 && (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="bg-[#C8A96B] text-[#07111F] hover:bg-[#d4b87a] disabled:opacity-50 rounded-lg px-6 py-2.5 text-xs uppercase tracking-[0.18em] font-medium flex items-center gap-1.5 transition"
                data-testid="launch-button"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Provisioning…</> : <>Launch workspace <ArrowUpRight size={14} /></>}
              </button>
            )}
          </div>
        </div>

        {/* Footer links */}
        <div className="text-center mt-8 text-xs text-white/40">
          Already have an account?{" "}
          <Link to="/login" className="text-[#C8A96B] hover:underline">Sign in</Link>
          <span className="mx-3 text-white/15">·</span>
          Compare plans →{" "}
          <Link to="/pricing" className="text-[#C8A96B] hover:underline">Pricing</Link>
        </div>
      </div>
    </PublicLayout>
  );
}
